import { Box } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import { useOnboarding } from "../../../contexts/OnboardingContext";
import { icons } from "../../../scenes/stack/environment/icons/iconExport";
import { ONBOARDING_STEPS } from "../../../utils/constants";
import TextButton from "../../buttons/TextButton";
const LearnBasicsNavigation = () => {
  const colors = useColors();
  const navigate = useNavigate();
  const location = useLocation();
  const { setOnboardingStage } = useOnboarding();
  const { trackEvent } = useAPI();
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const isProgrammaticNavigation = useRef(false);

  // Learning sections with descriptions - memoized to avoid recreating on every render
  const learningSections = useMemo(
    () => [
      {
        title: "Controls & Gaps",
        description:
          "View the gaps and filter them by tool, severity and more.",
        path: "/onboarding/posture/controls",
        navigationPath: "/posture/controls",
        navigationName: "Risk - Controls",
        placeholderColor: "#E3F2FD",
        image: "controls_page.svg",
      },
      {
        title: "Attack Surface",
        description:
          "View detailed visualization of your complete attack surface, mapped to potential risk vectors and threats with remediations for each vector.",
        navigationPath: "/posture/surface",
        navigationName: "Risk - Attack Surface",
        placeholderColor: "#E3F2FD",
        image: "attacksurface_page.svg",
      },

      {
        title: "Inventory",
        description:
          "get an overview of your inventory or focus your view by filtering source or smart filters to enhance security for threatened entities.",
        path: "/onboarding/inventory/environment",
        navigationPath: "/inventory/environment",
        navigationName: "Assets - Inventory",
        placeholderColor: "#FFF3E0",
        image: "inventory_page.svg",
      },
      {
        title: "Assets",
        description:
          "auto-created by Breeze — or define your own to focus protection efforts where they matter most.",
        path: "/onboarding/inventory/assets",
        navigationPath: "/inventory/assets",
        navigationName: "Assets - Critical Assets",
        placeholderColor: "#FFF3E0",
        image: "critical_asset_page.svg",
      },
      {
        title: "Integrations",
        description:
          " connect additional tools to gain a broader view of your environment.",
        path: "/onboarding/inventory/integrations",
        navigationPath: "/inventory/integrations",
        navigationName: "Assets - Integrations",
        placeholderColor: "#E3F2FD",
        image: "integrations_page.svg",
      },
      {
        title: "Settings",
        description:
          "customize Breeze to suit your team — invite members, set up SSO, and configure notification preferences.",
        path: "/onboarding/account/settings",
        navigationPath: "/settings",
        navigationName: "Account",
        placeholderColor: "#F3E5F5",
        image: "settings_page.svg",
      },
    ],
    []
  );

  // Sync current section index with URL on mount and when location changes
  useEffect(() => {
    // Skip if this is programmatic navigation
    if (isProgrammaticNavigation.current) {
      isProgrammaticNavigation.current = false;
      return;
    }

    const currentPath = location.pathname;
    const sectionIndex = learningSections.findIndex(
      (section) => section.path === currentPath
    );

    if (sectionIndex !== -1 && sectionIndex !== currentSectionIndex) {
      setCurrentSectionIndex(sectionIndex);
    }
  }, [location.pathname, currentSectionIndex, learningSections]);

  const handleNavigateTo = (path) => {
    trackEvent(`User navigated to ${path}`);
    // Keep the onboarding modal open and navigate directly
    isProgrammaticNavigation.current = true;
    navigate(path);
  };

  const handleNext = () => {
    if (currentSectionIndex < learningSections.length - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
      trackEvent(
        `User viewed ${
          learningSections[currentSectionIndex + 1].title
        } in onboarding`
      );
      handleNavigateTo(learningSections[currentSectionIndex + 1].path);
    } else {
      trackEvent("User completed onboarding");
      navigate("/");
      setOnboardingStage(ONBOARDING_STEPS.COMPLETED);
    }
  };

  const handleBack = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
      trackEvent(
        `User went back to ${
          learningSections[currentSectionIndex - 1].title
        } in onboarding`
      );
      handleNavigateTo(learningSections[currentSectionIndex - 1].path);
    } else {
      trackEvent("User went back to previous step");
    }
  };

  const handleComplete = () => {
    trackEvent("User completed onboarding");
    setOnboardingStage(ONBOARDING_STEPS.COMPLETED);
  };
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <TextButton
        color="inherit"
        size="small"
        variant="contained"
        onClick={handleBack}
        startIcon={icons.ArrowBackIcon}
        sx={{
          color: colors.text_secondary,
        }}
      >
        Back
      </TextButton>

      <Box>
        <TextButton
          onClick={handleComplete}
          sx={{
            textTransform: "none",
            mr: 2,
            color: colors.text_secondary,
          }}
        >
          Skip
        </TextButton>
        <TextButton
          color="inherit"
          size="small"
          variant="contained"
          endIcon={
            currentSectionIndex < learningSections.length - 1
              ? icons.arrow
              : null
          }
          onClick={
            currentSectionIndex < learningSections.length - 1
              ? handleNext
              : handleComplete
          }
          sx={{
            py: 1.5,
            px: 3,
            borderRadius: "8px",
            textTransform: "none",
            color: colors.text_secondary,
          }}
        >
          {currentSectionIndex < learningSections.length - 1
            ? "Next"
            : "Complete"}
        </TextButton>
      </Box>
    </Box>
  );
};

export default LearnBasicsNavigation;
